import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Box,
  TextField,
  Button,
  IconButton,
  CircularProgress,
  InputAdornment,
  Typography,
  Stack,
  Snackbar,
  Alert,
  Paper,
  Grid,
  Tooltip,
  MenuItem,
  Tabs,
  Tab,
  Checkbox,
  FormControlLabel,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  Close as CloseIcon,
  CloudUpload as CloudUploadIcon,
  Image as ImageIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  DeleteOutline as DeleteOutlineIcon,
  PersonAddRounded as PersonAddRoundedIcon,
  RemoveCircleOutline as RemoveCircleOutlineIcon,
  LockOutlined as LockOutlinedIcon,
} from "@mui/icons-material";
import axios from "axios";
import { CameraCaptureDialog, normalizeImageFile } from "../../utils/imageCapture";
import { mobileFieldProps, sanitizeMobileInput } from "./pawnCustomer/shared";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:5001";

// ─── Blue Theme Design Tokens ───────────────────────────────────────────────
const BLUE = "#2563EB";
const BLUE_DARK = "#1D4ED8";
const BLUE_MED = "#3B82F6";
const BLUE_LIGHT = "#DBEAFE";
const BLUE_PALE = "#EFF6FF";
const BLUE_SUBTLE = "#F0F7FF";

const INK = "#0F172A";
const INK_SOFT = "#334155";
const INK_MUTED = "#64748B";
const BORDER = "#E2E8F0";
const BORDER_MED = "#CBD5E1";
const SURFACE = "#FFFFFF";
const BG = "#F8FAFC";
const BG_ALT = "#F1F5F9";

const SUCCESS = "#059669";
const SUCCESS_BG = "#ECFDF5";
const ERROR_C = "#DC2626";
const ERROR_BG = "#FEF2F2";

// ─── Validation helpers ───────────────────────────────────────────────────────────
// Mobile numbers are handled by sanitizeMobileInput / mobileFieldProps from
// pawnCustomer/shared, so the rule is the same here as on the Customer Master.
const validateAadhar = (value) => {
  const cleaned = value?.toString().replace(/\D/g, "") || "";
  if (cleaned.length === 0) return { isValid: true, value: "", error: "" };
  if (cleaned.length > 12)
    return {
      isValid: false,
      value: cleaned.slice(0, 12),
      error: "Aadhar number must be exactly 12 digits",
    };
  if (cleaned.length === 12)
    return { isValid: true, value: cleaned, error: "" };
  return { isValid: true, value: cleaned, error: "" };
};

const validatePAN = (value) => {
  const cleaned = value?.toString().toUpperCase() || "";
  const alphanumeric = cleaned.replace(/[^A-Z0-9]/g, "");
  if (alphanumeric.length === 0) return { isValid: true, value: "", error: "" };
  if (alphanumeric.length > 10)
    return {
      isValid: false,
      value: alphanumeric.slice(0, 10),
      error: "PAN card must be exactly 10 alphanumeric characters",
    };
  if (alphanumeric.length === 10) {
    const panPattern = /^[A-Z]{5}[0-9]{4}[A-Z]$/;
    if (!panPattern.test(alphanumeric)) {
      return {
        isValid: true,
        value: alphanumeric,
        error: "Invalid PAN format (e.g., ABCDE1234F)",
      };
    }
    return { isValid: true, value: alphanumeric, error: "" };
  }
  return { isValid: true, value: alphanumeric, error: "" };
};

// ─── Field styling ───────────────────────────────────────────────────────────
const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    backgroundColor: SURFACE,
    fontSize: "0.875rem",
    "& fieldset": { borderColor: BORDER },
    "&:hover fieldset": { borderColor: BLUE_MED },
    "&.Mui-focused fieldset": { borderColor: BLUE, borderWidth: "2px" },
  },
  "& .MuiInputLabel-root": { color: INK_MUTED, fontSize: "0.8rem" },
  "& .MuiInputLabel-root.Mui-focused": { color: BLUE_DARK },
};

// ---------- Default Documents ----------
const DEFAULT_PAWN_DOCUMENTS = [
  { name: "PAN", number: "", file: null, fileName: "", renewalDate: "" },
  { name: "Aadhar", number: "", file: null, fileName: "", renewalDate: "" },
  { name: "Photo", number: "", file: null, fileName: "", renewalDate: "" },
];

// ---------- Image Source Dialog (Camera / Gallery) ----------
const ImageSourceDialog = ({ open, index, prefix = "", onClose, onTakePhoto }) => (
  <Dialog
    open={open}
    onClose={onClose}
    PaperProps={{
      sx: { borderRadius: 3, width: "100%", maxWidth: 320, mx: "auto" },
    }}
  >
    <DialogTitle
      sx={{ textAlign: "center", pb: 1, fontWeight: 700, color: INK }}
    >
      Choose Image Source
    </DialogTitle>
    <DialogContent sx={{ pb: 2 }}>
      <Stack spacing={1.5}>
        <Button
          fullWidth
          variant="outlined"
          startIcon={<CloudUploadIcon />}
          onClick={() => {
            onClose();
            // Opens the live camera (webcam on desktop, device camera on mobile).
            onTakePhoto?.();
          }}
          sx={{
            py: 1.5,
            borderRadius: 2,
            borderColor: BORDER,
            color: INK_SOFT,
            "&:hover": { borderColor: BLUE, color: BLUE, bgcolor: BLUE_PALE },
          }}
        >
          Take Photo (Camera)
        </Button>
        <Button
          fullWidth
          variant="outlined"
          startIcon={<ImageIcon />}
          onClick={() => {
            const el = document.getElementById(
              `${prefix}gallery-input-${index}`,
            );
            if (el) el.click();
            onClose();
          }}
          sx={{
            py: 1.5,
            borderRadius: 2,
            borderColor: BORDER,
            color: INK_SOFT,
            "&:hover": { borderColor: BLUE, color: BLUE, bgcolor: BLUE_PALE },
          }}
        >
          Choose from Gallery
        </Button>
      </Stack>
    </DialogContent>
  </Dialog>
);

// ---------- Simple Document Viewer Dialog ----------
const DocumentViewerDialog = ({ doc, onClose }) => (
  <Dialog open={!!doc} onClose={onClose} maxWidth="md" fullWidth>
    <DialogTitle sx={{ bgcolor: BLUE, color: "#fff" }}>
      {doc?.name || "Document"}
      <IconButton
        onClick={onClose}
        sx={{ position: "absolute", right: 8, top: 8, color: "#fff" }}
      >
        <CloseIcon />
      </IconButton>
    </DialogTitle>
    <DialogContent sx={{ textAlign: "center", p: 2 }}>
      {doc?.file && doc.file !== "No file" && (
        <Box
          component="img"
          src={doc.file}
          alt={doc.fileName}
          sx={{ maxWidth: "100%", maxHeight: "70vh" }}
        />
      )}
    </DialogContent>
  </Dialog>
);

// ---------- Reusable documents grid (shared by Customer + Nominee tabs) ----------
const DocumentsSection = ({ documents, setDocuments, prefix, setViewerDoc }) => {
  const [imageSourceDialog, setImageSourceDialog] = useState({
    open: false,
    index: null,
  });
  // Index of the document currently capturing from the live camera.
  const [cameraIndex, setCameraIndex] = useState(null);

  const setDocFile = (i, dataUrl, fileName) => {
    setDocuments((prev) => {
      const docs = [...prev];
      docs[i] = { ...docs[i], file: dataUrl, fileName };
      return docs;
    });
  };

  const updateDocument = (i, field, value) => {
    let processedValue = value;
    const doc = documents[i];
    if (field === "number") {
      if (doc.name === "Aadhar") processedValue = validateAadhar(value).value;
      else if (doc.name === "PAN") processedValue = validatePAN(value).value;
    }
    setDocuments((prev) => {
      const docs = [...prev];
      docs[i] = { ...docs[i], [field]: processedValue };
      return docs;
    });
  };

  const handleFileUpload = async (i, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Bake in EXIF orientation so phone photos don't come out rotated.
    const dataUrl = await normalizeImageFile(file);
    setDocFile(i, dataUrl, file.name);
    // Allow re-selecting the same file later.
    e.target.value = "";
  };

  const handleAddOtherDocument = () => {
    if (documents.some((doc) => doc.name === "Other")) return;
    setDocuments((prev) => [
      ...prev,
      { name: "Other", number: "", file: null, fileName: "", renewalDate: "" },
    ]);
  };

  const handleRemoveOtherDocument = () => {
    setDocuments((prev) => prev.filter((doc) => doc.name !== "Other"));
  };

  const hasOtherDocument = documents.some((doc) => doc.name === "Other");

  return (
    <Box sx={{ mt: 1 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          mb: 2.5,
          pb: 1,
          borderBottom: `2px solid ${BLUE_LIGHT}`,
        }}
      >
        <Box sx={{ width: 4, height: 24, bgcolor: BLUE, borderRadius: 2 }} />
        <Typography
          variant="h6"
          fontWeight={700}
          sx={{ color: BLUE_DARK, fontSize: "1rem" }}
        >
          Documents
        </Typography>
        <Box sx={{ flex: 1 }} />
        {!hasOtherDocument ? (
          <Button
            size="small"
            variant="outlined"
            startIcon={<PersonAddRoundedIcon />}
            onClick={handleAddOtherDocument}
            sx={{
              textTransform: "none",
              borderRadius: 2,
              color: BLUE,
              borderColor: BLUE,
              "&:hover": { bgcolor: BLUE_PALE, borderColor: BLUE_DARK },
            }}
          >
            Add Another Document
          </Button>
        ) : (
          <Button
            size="small"
            variant="outlined"
            color="error"
            startIcon={<RemoveCircleOutlineIcon />}
            onClick={handleRemoveOtherDocument}
            sx={{ textTransform: "none", borderRadius: 2 }}
          >
            Remove Other
          </Button>
        )}
      </Box>

      <Grid container spacing={2.5}>
        {documents.map((doc, i) => (
          <Grid item xs={12} sm={6} md={4} key={i}>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: 3,
                transition: "all 0.3s ease",
                border: doc.file
                  ? `1px solid ${BLUE_MED}60`
                  : `1px solid ${BORDER}`,
                backgroundColor: doc.file ? BLUE_SUBTLE : SURFACE,
                height: "100%",
                display: "flex",
                flexDirection: "column",
                "&:hover": {
                  boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                  borderColor: BLUE_MED,
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 2,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  {doc.file ? (
                    <CheckCircleIcon sx={{ fontSize: 20, color: SUCCESS }} />
                  ) : (
                    <ImageIcon sx={{ fontSize: 20, color: BLUE_MED }} />
                  )}
                  <Typography
                    variant="subtitle2"
                    fontWeight={700}
                    sx={{ color: BLUE_DARK }}
                  >
                    {doc.name}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                  {doc.name === "Other" && (
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() =>
                        setDocuments((prev) =>
                          prev.filter((_, idx) => idx !== i),
                        )
                      }
                      sx={{ p: 0.5 }}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  )}
                  <Tooltip title="Replace image">
                    <IconButton
                      size="small"
                      onClick={() =>
                        setImageSourceDialog({ open: true, index: i })
                      }
                      sx={{
                        color: BLUE,
                        backgroundColor: `${BLUE}10`,
                        "&:hover": { backgroundColor: `${BLUE}20` },
                      }}
                    >
                      <RefreshIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>

              <TextField
                size="small"
                fullWidth
                label="Document Number / ID"
                value={doc.number}
                onChange={(e) => updateDocument(i, "number", e.target.value)}
                placeholder={
                  doc.name === "Photo"
                    ? "Auto-filled from mobile number"
                    : `Enter ${doc.name} number`
                }
                disabled={doc.name === "Photo"}
                sx={{
                  mb: 2,
                  ...fieldSx,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    backgroundColor: doc.name === "Photo" ? BG_ALT : SURFACE,
                  },
                }}
              />

              <Box
                sx={{
                  position: "relative",
                  width: "100%",
                  height: 140,
                  borderRadius: 2,
                  border: `2px dashed ${doc.file ? BLUE_MED : BORDER_MED}`,
                  backgroundColor: doc.file ? BLUE_SUBTLE : BG,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  overflow: "hidden",
                  transition: "all 0.3s ease",
                  "&:hover": { borderColor: BLUE, backgroundColor: BLUE_PALE },
                }}
                onClick={() => {
                  if (doc.file && doc.file !== "No file") setViewerDoc(doc);
                  else setImageSourceDialog({ open: true, index: i });
                }}
              >
                {doc.file && doc.file !== "No file" ? (
                  <Box
                    component="img"
                    src={doc.file}
                    alt={`${doc.name} preview`}
                    sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                ) : (
                  <>
                    <CloudUploadIcon
                      sx={{ fontSize: 36, color: BLUE_MED, opacity: 0.8 }}
                    />
                    <Typography
                      variant="caption"
                      sx={{
                        color: INK_MUTED,
                        textAlign: "center",
                        px: 1,
                        mt: 1,
                      }}
                    >
                      Tap to upload
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ fontSize: 10, color: INK_MUTED }}
                    >
                      (Camera or Gallery)
                    </Typography>
                  </>
                )}
              </Box>

              {/* Hidden file inputs for camera / gallery */}
              <input
                id={`${prefix}camera-input-${i}`}
                type="file"
                hidden
                accept="image/*"
                capture="environment"
                onChange={(e) => handleFileUpload(i, e)}
              />
              <input
                id={`${prefix}gallery-input-${i}`}
                type="file"
                hidden
                accept="image/*"
                onChange={(e) => handleFileUpload(i, e)}
              />
            </Paper>
          </Grid>
        ))}
      </Grid>

      <ImageSourceDialog
        open={imageSourceDialog.open}
        index={imageSourceDialog.index}
        prefix={prefix}
        onClose={() => setImageSourceDialog({ open: false, index: null })}
        onTakePhoto={() => setCameraIndex(imageSourceDialog.index)}
      />

      <CameraCaptureDialog
        open={cameraIndex !== null}
        onClose={() => setCameraIndex(null)}
        onCapture={(dataUrl) => {
          if (cameraIndex !== null)
            setDocFile(cameraIndex, dataUrl, "camera-photo.jpg");
        }}
      />
    </Box>
  );
};

// ---------- OTP Dialog ----------
const OTPDialog = ({ open, onClose, mobile, store_id, onVerifySuccess }) => {
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [timer, setTimer] = useState(30);
  const [isVerifying, setIsVerifying] = useState(false);
  const inputsRef = useRef([]);
  const timerIntervalRef = useRef(null);
  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  useEffect(() => {
    if (open) {
      setOtp(["", "", "", "", "", ""]);
      setTimer(30);
      setIsVerifying(false);
      setTimeout(() => inputsRef.current[0]?.focus(), 100);
    } else if (timerIntervalRef.current)
      clearInterval(timerIntervalRef.current);
  }, [open]);

  useEffect(() => {
    if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
    if (open && timer > 0 && !isVerifying) {
      timerIntervalRef.current = setInterval(
        () =>
          setTimer((prev) =>
            prev <= 1 ? (clearInterval(timerIntervalRef.current), 0) : prev - 1,
          ),
        1000,
      );
    }
    return () => {
      if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
    };
  }, [open, timer, isVerifying]);

  const handleChange = (e, idx) => {
    const val = e.target.value.replace(/\D/g, "").slice(0, 1);
    const newOtp = [...otp];
    newOtp[idx] = val;
    setOtp(newOtp);
    if (val && idx < 5) inputsRef.current[idx + 1]?.focus();
  };

  const handleKeyDown = (e, idx) => {
    if (e.key === "Backspace") {
      e.preventDefault();
      const newOtp = [...otp];
      if (otp[idx]) newOtp[idx] = "";
      else if (idx > 0) {
        newOtp[idx - 1] = "";
        inputsRef.current[idx - 1]?.focus();
      }
      setOtp(newOtp);
    }
    if (e.key === "Enter") handleVerify();
  };

  const handleVerify = async () => {
    if (otp.some((d) => !d)) {
      setSnack({ open: true, message: "Enter full OTP", severity: "error" });
      return;
    }
    setIsVerifying(true);
    try {
      const res = await axios.post(
        `${API_BASE_URL}/api/pawn/customers/verify-otp`,
        {
          mobile,
          otp: otp.join(""),
        },
      );

      if (res.data.statusCode === 200 && res.data.success) {
        if (res.data.token) {
          localStorage.setItem("customer_otp_token", res.data.token);
        }

        setSnack({ open: true, message: "OTP verified!", severity: "success" });
        if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
        setTimeout(() => {
          onVerifySuccess();
          onClose();
        }, 1000);
      } else {
        setSnack({
          open: true,
          message: res.data.message || "Invalid OTP",
          severity: "warning",
        });
        setOtp(["", "", "", "", "", ""]);
        setIsVerifying(false);
        setTimeout(() => inputsRef.current[0]?.focus(), 100);
      }
    } catch (err) {
      setSnack({
        open: true,
        message: "Verification failed",
        severity: "error",
      });
      setIsVerifying(false);
    }
  };

  const handleResend = async () => {
    setOtp(["", "", "", "", "", ""]);
    setTimer(30);
    setIsVerifying(false);
    try {
      const res = await axios.post(
        `${API_BASE_URL}/api/pawn/customers/send-otp`,
        {
          mobile,
          store_id,
        },
      );
      if (res.data.statusCode === 1 || res.data.success)
        setSnack({ open: true, message: "OTP resent", severity: "success" });
      else
        setSnack({
          open: true,
          message: res.data.statusMessage || "Failed",
          severity: "error",
        });
    } catch {
      setSnack({ open: true, message: "Resend failed", severity: "error" });
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={(e, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") return;
          onClose();
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700, color: INK }}>
          Verify Mobile Number
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: INK_MUTED }}>
            Enter 6‑digit OTP sent to {mobile}
          </DialogContentText>
          <Box
            sx={{
              display: "flex",
              gap: { xs: 0.75, sm: 1.5 },
              justifyContent: "center",
              my: 2,
            }}
          >
            {otp.map((digit, idx) => (
              <TextField
                key={idx}
                inputRef={(el) => (inputsRef.current[idx] = el)}
                value={digit}
                onChange={(e) => handleChange(e, idx)}
                onKeyDown={(e) => handleKeyDown(e, idx)}
                inputProps={{
                  style: { textAlign: "center", fontSize: 24 },
                }}
                disabled={isVerifying}
                sx={[fieldSx, { width: { xs: 34, sm: 46 } }]}
              />
            ))}
          </Box>
          <Typography
            variant="caption"
            display="block"
            textAlign="center"
            sx={{ color: INK_MUTED }}
          >
            {timer > 0 ? (
              `OTP valid for ${timer}s`
            ) : (
              <span
                style={{ cursor: "pointer", color: BLUE }}
                onClick={handleResend}
              >
                Resend OTP
              </span>
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} sx={{ color: INK_MUTED }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleVerify}
            disabled={isVerifying || timer === 0}
            sx={{ bgcolor: BLUE, "&:hover": { bgcolor: BLUE_DARK } }}
          >
            {isVerifying ? <CircularProgress size={20} /> : "Verify"}
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((prev) => ({ ...prev, open: false }))}
      >
        <Alert severity={snack.severity} variant="filled">
          {snack.message}
        </Alert>
      </Snackbar>
    </>
  );
};

// ---------- Main combined Customer + Nominee dialog ----------
const NewCustomerDialog = ({
  open,
  onClose,
  onCustomerCreated,
  onNomineeCreated,
  storeId,
  userId,
  showSnackbar,
  // Whether creating a customer requires OTP verification. Driven by the store's
  // "OTP validation for customer creation" pawn setting. When false, the customer
  // is created directly without the OTP step.
  otpRequired = true,
  // The store's "Allow duplicate mobile numbers" setting. When on, a repeated
  // mobile no longer blocks the save — it asks whether this is a different
  // person (families share one phone; they are told apart by Aadhar / PAN).
  allowDuplicateMobile = false,
  // When provided the dialog opens straight on the Nominee tab to add a
  // nominee to an already-existing customer (the "Add Nominee" flow).
  existingCustomerId = null,
  existingCustomerName = "",
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [tab, setTab] = useState(0);
  // The customer a nominee can be attached to: either freshly created in this
  // dialog or passed in via existingCustomerId.
  const [activeCustomerId, setActiveCustomerId] = useState(null);
  const [activeCustomerName, setActiveCustomerName] = useState("");
  // After a customer is created, ask whether to continue to the Nominee tab
  // (Yes) or close the dialog with the new customer loaded (No).
  const [nomineePromptOpen, setNomineePromptOpen] = useState(false);

  // ── Customer form state ──────────────────────────────────────────────────
  const [form, setForm] = useState({
    name: "",
    mobile: "",
    address: "",
    relationType: "",
    relationName: "",
  });
  const [errors, setErrors] = useState({});
  const [sendingOtp, setSendingOtp] = useState(false);
  const [otpDialogOpen, setOtpDialogOpen] = useState(false);
  const [tempCustomerData, setTempCustomerData] = useState(null);
  const [duplicateMobile, setDuplicateMobile] = useState(null);
  const [isCheckingMobile, setIsCheckingMobile] = useState(false);
  const mobileCheckTimeoutRef = useRef(null);
  const [documents, setDocuments] = useState(
    JSON.parse(JSON.stringify(DEFAULT_PAWN_DOCUMENTS)),
  );

  // ── Nominee form state ───────────────────────────────────────────────────
  const [nomForm, setNomForm] = useState({
    name: "",
    mobile: "",
    address: "",
    relation: "",
    sharePercent: "",
    isPrimary: false,
    note: "",
  });
  const [nomErrors, setNomErrors] = useState({});
  const [isSavingNominee, setIsSavingNominee] = useState(false);
  const [nomDocuments, setNomDocuments] = useState(
    JSON.parse(JSON.stringify(DEFAULT_PAWN_DOCUMENTS)),
  );
  const [nomDuplicateMobile, setNomDuplicateMobile] = useState(null);
  const [isCheckingNomMobile, setIsCheckingNomMobile] = useState(false);
  const nomMobileCheckTimeoutRef = useRef(null);

  const [viewerDoc, setViewerDoc] = useState(null);

  // Reset everything when (re)opened.
  useEffect(() => {
    if (open) {
      setForm({
        name: "",
        mobile: "",
        address: "",
        relationType: "",
        relationName: "",
      });
      setErrors({});
      setDuplicateMobile(null);
      setIsCheckingMobile(false);
      setSendingOtp(false);
      setOtpDialogOpen(false);
      setTempCustomerData(null);
      setDocuments(JSON.parse(JSON.stringify(DEFAULT_PAWN_DOCUMENTS)));

      setNomForm({
        name: "",
        mobile: "",
        address: "",
        relation: "",
        sharePercent: "",
        isPrimary: false,
        note: "",
      });
      setNomErrors({});
      setNomDuplicateMobile(null);
      setNomDocuments(JSON.parse(JSON.stringify(DEFAULT_PAWN_DOCUMENTS)));
      setNomineePromptOpen(false);

      // If launched to add a nominee to an existing customer, start there.
      if (existingCustomerId) {
        setActiveCustomerId(existingCustomerId);
        setActiveCustomerName(existingCustomerName || "");
        setTab(1);
      } else {
        setActiveCustomerId(null);
        setActiveCustomerName("");
        setTab(0);
      }

      if (mobileCheckTimeoutRef.current)
        clearTimeout(mobileCheckTimeoutRef.current);
      if (nomMobileCheckTimeoutRef.current)
        clearTimeout(nomMobileCheckTimeoutRef.current);
    }
  }, [open, existingCustomerId, existingCustomerName]);

  // Auto-fill Photo document number from mobile (customer + nominee).
  useEffect(() => {
    if (form.mobile)
      setDocuments((prev) =>
        prev.map((d) =>
          d.name === "Photo" ? { ...d, number: form.mobile } : d,
        ),
      );
  }, [form.mobile]);
  useEffect(() => {
    if (nomForm.mobile)
      setNomDocuments((prev) =>
        prev.map((d) =>
          d.name === "Photo" ? { ...d, number: nomForm.mobile } : d,
        ),
      );
  }, [nomForm.mobile]);

  const checkMobileDuplicate = useCallback(
    async (number, setter, setChecking, timeoutRef) => {
      const clean = (number || "").toString().replace(/\D/g, "");
      if (clean.length < 4) {
        setter(null);
        return;
      }
      if (storeId === null || storeId === undefined) return;
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(async () => {
        try {
          setChecking(true);
          const res = await axios.get(
            `${API_BASE_URL}/api/pawn/customers/check-duplicate/${storeId}`,
            { params: { numbers: clean } },
          );
          if (res.data.success) {
            const result = res.data.data;
            setter(result.isDuplicate ? result.existingCustomer : null);
          }
        } catch (error) {
          console.error("Duplicate mobile check error:", error);
          setter(null);
        } finally {
          setChecking(false);
        }
      }, 600);
    },
    [storeId],
  );

  // ── Customer handlers ────────────────────────────────────────────────────
  const handleChange = (field, value) => {
    let processedValue = value;
    if (field === "mobile") processedValue = sanitizeMobileInput(value);
    setForm((prev) => ({ ...prev, [field]: processedValue }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: false }));
    if (field === "mobile") {
      setDuplicateMobile(null);
      checkMobileDuplicate(
        processedValue,
        setDuplicateMobile,
        setIsCheckingMobile,
        mobileCheckTimeoutRef,
      );
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!form.name.trim()) newErrors.name = "Name is required";
    if (!form.mobile.trim()) newErrors.mobile = "Mobile is required";
    if (!form.address.trim()) newErrors.address = "Address is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const buildDocumentsPayload = (docs) =>
    docs
      .filter((doc) => {
        if (doc.name === "Other") {
          const hasFile = doc.file && doc.file !== "No file";
          const hasNumber = doc.number?.trim();
          return hasFile || hasNumber;
        }
        return true;
      })
      .map((doc) => ({
        name: doc.name,
        number: doc.number,
        file: doc.file,
        fileName: doc.fileName,
        renewalDate: doc.renewalDate || null,
      }));

  const sendOtpAndCreate = async () => {
    if (!validate()) return;
    if (duplicateMobile && !allowDuplicateMobile) return;

    setSendingOtp(true);
    try {
      const otpRes = await axios.post(
        `${API_BASE_URL}/api/pawn/customers/send-otp`,
        { mobile: form.mobile, store_id: storeId },
      );
      if (!otpRes.data.success) {
        throw new Error(otpRes.data.statusMessage || "Failed to send OTP");
      }
      setTempCustomerData({
        ...form,
        store_id: storeId,
        created_by: userId,
        documents: buildDocumentsPayload(documents),
        relative_relation: form.relationType || "None",
        relative_name: form.relationName || null,
      });
      setOtpDialogOpen(true);
    } catch (err) {
      showSnackbar?.(err.message || "Could not send OTP", "error");
    } finally {
      setSendingOtp(false);
    }
  };

  // Direct creation path used when the store setting disables OTP validation.
  // Skips the OTP send/verify round-trip and creates the customer immediately.
  const createCustomerDirect = async () => {
    if (!validate()) return;
    if (duplicateMobile && !allowDuplicateMobile) return;

    setSendingOtp(true);
    try {
      const payload = {
        store_id: storeId,
        created_by: userId,
        name: form.name,
        mobile: form.mobile,
        pickup_address: form.address,
        relative_relation: form.relationType || "None",
        relative_name: form.relationName || null,
        documents: buildDocumentsPayload(documents),
        nominees: [],
      };

      const res = await axios.post(
        `${API_BASE_URL}/api/pawn/customers`,
        payload,
      );
      if (!res.data.success) {
        throw new Error(res.data.statusMessage || "Creation failed");
      }

      const created = res.data.data;
      onCustomerCreated(created);

      // Keep the dialog open and ask whether to continue with a nominee for
      // the customer just created, or close with the customer loaded.
      setActiveCustomerId(created.id || created.customer_id);
      setActiveCustomerName(created.name || "");
      setNomineePromptOpen(true);
      if (showSnackbar)
        showSnackbar(`Customer "${created.name}" created.`, "success");
    } catch (err) {
      showSnackbar?.(err.message || "Customer creation failed", "error");
    } finally {
      setSendingOtp(false);
    }
  };

  const handleOtpVerified = async () => {
    if (!tempCustomerData) return;
    try {
      const payload = {
        store_id: tempCustomerData.store_id,
        created_by: tempCustomerData.created_by,
        name: tempCustomerData.name,
        mobile: tempCustomerData.mobile,
        pickup_address: tempCustomerData.address,
        relative_relation: tempCustomerData.relative_relation || "None",
        relative_name: tempCustomerData.relative_name,
        documents: tempCustomerData.documents,
        nominees: [],
        otp_verified: true,
        otp_token: localStorage.getItem("customer_otp_token"),
      };

      const res = await axios.post(
        `${API_BASE_URL}/api/pawn/customers/create-with-verification`,
        payload,
      );
      if (!res.data.success) {
        throw new Error(res.data.statusMessage || "Creation failed");
      }

      const created = res.data.data;
      // Select the customer in the parent form.
      onCustomerCreated(created);
      localStorage.removeItem("customer_otp_token");

      // Keep the dialog open and ask whether to continue with a nominee for
      // the customer just created, or close with the customer loaded.
      setActiveCustomerId(created.id || created.customer_id);
      setActiveCustomerName(created.name || "");
      setNomineePromptOpen(true);
      if (showSnackbar)
        showSnackbar(`Customer "${created.name}" created.`, "success");
    } catch (err) {
      showSnackbar?.(err.message || "Customer creation failed", "error");
    }
  };

  // ── Nominee handlers ─────────────────────────────────────────────────────
  const handleNomChange = (field, value) => {
    let processedValue = value;
    if (field === "mobile") processedValue = sanitizeMobileInput(value);
    setNomForm((prev) => ({ ...prev, [field]: processedValue }));
    if (nomErrors[field]) setNomErrors((prev) => ({ ...prev, [field]: false }));
    if (field === "mobile") {
      setNomDuplicateMobile(null);
      checkMobileDuplicate(
        processedValue,
        setNomDuplicateMobile,
        setIsCheckingNomMobile,
        nomMobileCheckTimeoutRef,
      );
    }
  };

  const validateNominee = () => {
    const newErrors = {};
    if (!nomForm.name.trim()) newErrors.name = "Name is required";
    if (!nomForm.mobile.trim()) newErrors.mobile = "Mobile is required";
    setNomErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSaveNominee = async () => {
    if (!activeCustomerId) return;
    if (!validateNominee()) {
      if (showSnackbar) showSnackbar("Please fill name and mobile", "error");
      return;
    }
    if (nomDuplicateMobile) {
      if (showSnackbar)
        showSnackbar(
          `Mobile already belongs to ${nomDuplicateMobile.name}`,
          "error",
        );
      return;
    }

    setIsSavingNominee(true);
    try {
      const nomineePayload = {
        name: nomForm.name,
        mobile: nomForm.mobile,
        pickup_address: nomForm.address || null,
        relation: nomForm.relation || null,
        share_percent: nomForm.sharePercent
          ? parseFloat(nomForm.sharePercent)
          : null,
        is_primary: nomForm.isPrimary ? 1 : 0,
        note: nomForm.note || null,
        documents: buildDocumentsPayload(nomDocuments),
      };

      const res = await axios.put(
        `${API_BASE_URL}/api/pawn/customers/${activeCustomerId}`,
        { store_id: storeId, nominees_to_add: [nomineePayload] },
      );
      if (!res.data.success) throw new Error(res.data.statusMessage);

      if (showSnackbar) showSnackbar("Nominee created successfully", "success");
      if (onNomineeCreated) onNomineeCreated();
      onClose();
    } catch (error) {
      console.error("Error creating nominee:", error);
      if (showSnackbar)
        showSnackbar(error.message || "Failed to create nominee", "error");
    } finally {
      setIsSavingNominee(false);
    }
  };

  const isCustomerSubmitDisabled =
    sendingOtp || (!!duplicateMobile && !allowDuplicateMobile);
  const nomineeLocked = !activeCustomerId;

  return (
    <>
      <Dialog
        open={open}
        // Only the ✕ / Cancel buttons close the dialog — ignore backdrop clicks
        // and Escape so a stray click doesn't discard everything entered.
        onClose={(e, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") return;
          onClose();
        }}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : 3,
            boxShadow: "0 8px 32px rgba(15,23,42,0.12)",
          },
        }}
      >
        <DialogTitle
          sx={{
            bgcolor: BLUE,
            color: "#fff",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            fontWeight: 700,
            fontSize: "1rem",
            borderBottom: `1px solid ${BORDER}`,
          }}
        >
          Create Customer &amp; Nominee
          <IconButton size="small" onClick={onClose} sx={{ color: "#fff" }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        {/* Tabs */}
        <Box sx={{ bgcolor: SURFACE, borderBottom: `1px solid ${BORDER}` }}>
          <Tabs
            value={tab}
            onChange={(e, v) => setTab(v)}
            variant="fullWidth"
            sx={{
              "& .MuiTab-root": {
                textTransform: "none",
                fontWeight: 700,
                fontSize: "0.9rem",
              },
              "& .Mui-selected": { color: `${BLUE_DARK} !important` },
              "& .MuiTabs-indicator": { backgroundColor: BLUE },
            }}
          >
            <Tab label="Customer" disabled={!!existingCustomerId} />
            <Tab
              label="Nominee"
              icon={
                nomineeLocked ? (
                  <LockOutlinedIcon sx={{ fontSize: 16 }} />
                ) : undefined
              }
              iconPosition="end"
            />
          </Tabs>
        </Box>

        <DialogContent
          dividers
          sx={{
            maxHeight: isMobile ? "100%" : "78vh",
            bgcolor: BG,
            p: { xs: 1.5, sm: 3 },
          }}
        >
          {/* ── CUSTOMER TAB ── */}
          {tab === 0 && (
            <>
              <Box sx={{ mb: 4 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mb: 2.5,
                    pb: 1,
                    borderBottom: `2px solid ${BLUE_LIGHT}`,
                  }}
                >
                  <Box
                    sx={{ width: 4, height: 24, bgcolor: BLUE, borderRadius: 2 }}
                  />
                  <Typography
                    variant="h6"
                    fontWeight={700}
                    sx={{ color: BLUE_DARK, fontSize: "1rem" }}
                  >
                    Customer Details
                  </Typography>
                </Box>

                {activeCustomerId && (
                  <Box
                    sx={{
                      mb: 2,
                      p: 1.25,
                      borderRadius: 2,
                      bgcolor: SUCCESS_BG,
                      border: `1px solid ${SUCCESS}33`,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <CheckCircleIcon sx={{ fontSize: 18, color: SUCCESS }} />
                    <Typography variant="caption" sx={{ color: INK_SOFT }}>
                      Customer already created. Switch to the <b>Nominee</b> tab
                      to add a nominee, or close this dialog.
                    </Typography>
                  </Box>
                )}

                <Grid container spacing={1.5}>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      required
                      fullWidth
                      size="small"
                      label="Full Name"
                      value={form.name}
                      onChange={(e) =>
                        handleChange("name", e.target.value.toUpperCase())
                      }
                      error={!!errors.name}
                      helperText={errors.name}
                      sx={fieldSx}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      required
                      fullWidth
                      size="small"
                      label="Mobile Number"
                      {...mobileFieldProps()}
                      value={form.mobile}
                      onChange={(e) => handleChange("mobile", e.target.value)}
                      error={
                        !!errors.mobile ||
                        (!!duplicateMobile && !allowDuplicateMobile)
                      }
                      helperText={
                        duplicateMobile
                          ? allowDuplicateMobile
                            ? `⚠️ Also used by "${duplicateMobile.name}" — fine if this is a different person; give them an Aadhar or PAN.`
                            : `⚠️ Already belongs to "${duplicateMobile.name}". Use a different number.`
                          : errors.mobile || undefined
                      }
                      sx={fieldSx}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            {isCheckingMobile ? (
                              <CircularProgress
                                size={16}
                                sx={{ color: BLUE }}
                              />
                            ) : duplicateMobile ? (
                              <Typography
                                sx={{ color: ERROR_C, fontSize: "1.1rem" }}
                              >
                                ✕
                              </Typography>
                            ) : form.mobile.replace(/\D/g, "").length >= 4 ? (
                              <Typography
                                sx={{ color: SUCCESS, fontSize: "1.1rem" }}
                              >
                                ✓
                              </Typography>
                            ) : null}
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={12} md={5}>
                    <TextField
                      required
                      fullWidth
                      size="small"
                      label="Address"
                      multiline
                      rows={2}
                      value={form.address}
                      onChange={(e) => handleChange("address", e.target.value)}
                      error={!!errors.address}
                      helperText={errors.address}
                      sx={fieldSx}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      label="Relation Type"
                      value={form.relationType}
                      onChange={(e) =>
                        handleChange("relationType", e.target.value)
                      }
                      sx={fieldSx}
                    >
                      <MenuItem value="">None</MenuItem>
                      <MenuItem value="S/o">S/o (Son of)</MenuItem>
                      <MenuItem value="D/o">D/o (Daughter of)</MenuItem>
                      <MenuItem value="W/o">W/o (Wife of)</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6} md={5}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Relative Name"
                      placeholder="Enter full name of relative"
                      value={form.relationName}
                      onChange={(e) =>
                        handleChange("relationName", e.target.value.toUpperCase())
                      }
                      sx={fieldSx}
                    />
                  </Grid>
                </Grid>
              </Box>

              <DocumentsSection
                documents={documents}
                setDocuments={setDocuments}
                prefix=""
                setViewerDoc={setViewerDoc}
              />
            </>
          )}

          {/* ── NOMINEE TAB ── */}
          {tab === 1 &&
            (nomineeLocked ? (
              <Box
                sx={{
                  textAlign: "center",
                  py: 8,
                  px: 2,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 1.5,
                }}
              >
                <LockOutlinedIcon sx={{ fontSize: 48, color: BORDER_MED }} />
                <Typography
                  variant="h6"
                  fontWeight={700}
                  sx={{ color: INK_SOFT }}
                >
                  Create a customer first
                </Typography>
                <Typography variant="body2" sx={{ color: INK_MUTED }}>
                  A nominee can only be added once its customer has been created.
                  {otpRequired
                    ? " Fill in the Customer tab and verify the OTP to continue."
                    : " Fill in the Customer tab to continue."}
                </Typography>
                <Button
                  variant="outlined"
                  onClick={() => setTab(0)}
                  sx={{
                    mt: 1,
                    textTransform: "none",
                    borderColor: BLUE,
                    color: BLUE,
                    "&:hover": { bgcolor: BLUE_PALE, borderColor: BLUE_DARK },
                  }}
                >
                  Go to Customer tab
                </Button>
              </Box>
            ) : (
              <>
                <Box sx={{ mb: 4 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 2.5,
                      pb: 1,
                      borderBottom: `2px solid ${BLUE_LIGHT}`,
                    }}
                  >
                    <Box
                      sx={{
                        width: 4,
                        height: 24,
                        bgcolor: BLUE,
                        borderRadius: 2,
                      }}
                    />
                    <Typography
                      variant="h6"
                      fontWeight={700}
                      sx={{ color: BLUE_DARK, fontSize: "1rem" }}
                    >
                      Nominee Details
                    </Typography>
                    {activeCustomerName && (
                      <Typography
                        variant="caption"
                        sx={{ color: INK_MUTED, ml: 1 }}
                      >
                        for {activeCustomerName}
                      </Typography>
                    )}
                  </Box>

                  <Grid container spacing={1.5}>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        required
                        fullWidth
                        size="small"
                        label="Full Name"
                        value={nomForm.name}
                        onChange={(e) =>
                          handleNomChange("name", e.target.value.toUpperCase())
                        }
                        error={!!nomErrors.name}
                        helperText={nomErrors.name}
                        sx={fieldSx}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <TextField
                        required
                        fullWidth
                        size="small"
                        label="Mobile Number"
                        {...mobileFieldProps()}
                        value={nomForm.mobile}
                        onChange={(e) =>
                          handleNomChange("mobile", e.target.value)
                        }
                        error={!!nomErrors.mobile || !!nomDuplicateMobile}
                        helperText={
                          nomDuplicateMobile
                            ? `⚠️ Already belongs to "${nomDuplicateMobile.name}"`
                            : nomErrors.mobile || undefined
                        }
                        sx={fieldSx}
                        InputProps={{
                          endAdornment: isCheckingNomMobile ? (
                            <InputAdornment position="end">
                              <CircularProgress size={16} sx={{ color: BLUE }} />
                            </InputAdornment>
                          ) : null,
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={12} md={5}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Address"
                        multiline
                        rows={2}
                        value={nomForm.address}
                        onChange={(e) =>
                          handleNomChange("address", e.target.value)
                        }
                        sx={fieldSx}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Relation (e.g. Son, Spouse)"
                        value={nomForm.relation}
                        onChange={(e) =>
                          handleNomChange("relation", e.target.value)
                        }
                        sx={fieldSx}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Share %"
                        type="number"
                        value={nomForm.sharePercent}
                        onChange={(e) =>
                          handleNomChange("sharePercent", e.target.value)
                        }
                        sx={fieldSx}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">%</InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={5}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={nomForm.isPrimary}
                            onChange={(e) =>
                              handleNomChange("isPrimary", e.target.checked)
                            }
                            sx={{
                              color: BLUE,
                              "&.Mui-checked": { color: BLUE_DARK },
                            }}
                          />
                        }
                        label="Primary Nominee"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Note (optional)"
                        multiline
                        rows={2}
                        value={nomForm.note}
                        onChange={(e) =>
                          handleNomChange("note", e.target.value)
                        }
                        sx={fieldSx}
                      />
                    </Grid>
                  </Grid>
                </Box>

                <DocumentsSection
                  documents={nomDocuments}
                  setDocuments={setNomDocuments}
                  prefix="nom-"
                  setViewerDoc={setViewerDoc}
                />
              </>
            ))}
        </DialogContent>

        <DialogActions
          sx={{
            px: 2.5,
            py: 1.75,
            gap: 1,
            bgcolor: SURFACE,
            borderTop: `1px solid ${BORDER}`,
          }}
        >
          <Button
            variant="outlined"
            onClick={onClose}
            // Once the customer exists, the Customer tab's buttons are locked so
            // the create API can't be fired again (finish via Nominee tab / ✕).
            disabled={tab === 0 && !!activeCustomerId}
            sx={{
              borderRadius: "8px",
              textTransform: "none",
              borderColor: BORDER,
              color: INK_MUTED,
              "&:hover": {
                borderColor: ERROR_C,
                color: ERROR_C,
                bgcolor: ERROR_BG,
              },
            }}
          >
            {tab === 1 && activeCustomerId ? "Done" : "Cancel"}
          </Button>
          {tab === 0 ? (
            <Button
              variant="contained"
              onClick={otpRequired ? sendOtpAndCreate : createCustomerDirect}
              disabled={isCustomerSubmitDisabled || !!activeCustomerId}
              sx={{
                borderRadius: "8px",
                textTransform: "none",
                bgcolor: BLUE,
                color: "#fff",
                fontWeight: 700,
                "&:hover": { bgcolor: BLUE_DARK },
                "&:disabled": { opacity: 0.5 },
                minWidth: 170,
              }}
            >
              {sendingOtp ? (
                <CircularProgress size={20} sx={{ color: "#fff" }} />
              ) : otpRequired ? (
                "Create & Verify OTP"
              ) : (
                "Create Customer"
              )}
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleSaveNominee}
              disabled={nomineeLocked || isSavingNominee}
              sx={{
                borderRadius: "8px",
                textTransform: "none",
                bgcolor: BLUE,
                color: "#fff",
                fontWeight: 700,
                "&:hover": { bgcolor: BLUE_DARK },
                "&:disabled": { opacity: 0.5 },
                minWidth: 150,
              }}
            >
              {isSavingNominee ? (
                <CircularProgress size={20} sx={{ color: "#fff" }} />
              ) : (
                "Add Nominee"
              )}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Post-creation choice: continue to the Nominee tab, or close the
          dialog and load the freshly created customer in the parent form. */}
      <Dialog
        open={nomineePromptOpen}
        // Force an explicit Yes/No — no backdrop/Escape dismissal.
        onClose={() => {}}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: INK }}>
          Customer Created
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: INK_MUTED }}>
            {activeCustomerName
              ? `"${activeCustomerName}" was created successfully. `
              : "Customer was created successfully. "}
            Continue creating a nominee next?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2 }}>
          <Button
            onClick={() => {
              setNomineePromptOpen(false);
              onClose();
            }}
            sx={{ color: INK_MUTED, textTransform: "none" }}
          >
            No, Close
          </Button>
          <Button
            variant="contained"
            autoFocus
            onClick={() => {
              setNomineePromptOpen(false);
              setTab(1);
            }}
            sx={{
              textTransform: "none",
              bgcolor: BLUE,
              fontWeight: 700,
              "&:hover": { bgcolor: BLUE_DARK },
            }}
          >
            Yes, Add Nominee
          </Button>
        </DialogActions>
      </Dialog>

      <OTPDialog
        open={otpDialogOpen}
        onClose={() => {
          setOtpDialogOpen(false);
          setTempCustomerData(null);
        }}
        mobile={form.mobile}
        store_id={storeId}
        onVerifySuccess={handleOtpVerified}
      />

      <DocumentViewerDialog doc={viewerDoc} onClose={() => setViewerDoc(null)} />
    </>
  );
};

export default NewCustomerDialog;
